import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Avatar,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Lock as LockIcon,
  Person as PersonIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { apiGet, apiPut, apiPost } from '../utils/apiUtils';
// Removed supabase import as we're using signOut from AuthContext instead

/**
 * User Profile Component
 * Displays and allows editing of user profile information
 */
const UserProfile = ({ onClose, onProfileUpdate }) => {
  const { user, userProfile, signOut } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Profile editing state
  const [isEditing, setIsEditing] = useState(false);
  const [profileData, setProfileData] = useState({
    display_name: '',
    title_role: '',
    hide_list_cleaner_results_tutorial: false,
    hide_activity_history_tutorial: false
  });
  
  // Password change state
  const [passwordDialog, setPasswordDialog] = useState(false);
  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  
  // Separate state for password dialog messages
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  
  // Load user profile data
  useEffect(() => {
    if (user) {
      loadProfileData();
    }
  }, [user]);
  
  const loadProfileData = async () => {
    if (!user?.id) return;
    
    setLoading(true);
    try {
      const response = await apiGet('/api/profile');
      
      if (!response.ok) {
        let errMsg = 'Failed to load profile data';
        try {
          const errorData = await response.json();
          if (errorData?.error) errMsg = errorData.error;
        } catch (parseError) {
          console.error('Error parsing error response:', parseError);
        }
        throw new Error(errMsg);
      }
      
      const data = await response.json();
      setProfileData({
        display_name: data.display_name || '',
        title_role: data.title_role || '',
        hide_list_cleaner_results_tutorial: data.hide_list_cleaner_results_tutorial ?? false,
        hide_activity_history_tutorial: data.hide_activity_history_tutorial ?? false
      });
      
    } catch (error) {
      console.error('Error loading profile:', error);
      setError('Failed to load profile data');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSaveProfile = async () => {
    if (!user?.id) return;
    
    setSaving(true);
    setError(null);
    
    try {
      const response = await apiPut('/api/profile', {
        display_name: profileData.display_name,
        title_role: profileData.title_role
        // Tutorial preferences auto-save individually, not included here
      });
      
      if (!response.ok) {
        let errMsg = 'Failed to update profile';
        try {
          const errorData = await response.json();
          if (errorData?.error) errMsg = errorData.error;
        } catch (parseError) {
          console.error('Error parsing error response:', parseError);
        }
        throw new Error(errMsg);
      }
      
      // Parse the successful response (optional, for future use)
      const result = await response.json();
      console.log('Profile updated successfully:', result);
      
      setSuccess('Profile updated successfully');
      setIsEditing(false);
      
      // Notify parent component that profile was updated
      if (onProfileUpdate && typeof onProfileUpdate === 'function') {
        onProfileUpdate();
      }
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
      
    } catch (error) {
      console.error('Error saving profile:', error);
      setError('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };
  
  // Auto-save tutorial preferences immediately when toggled
  const handleTutorialToggle = async (field, value) => {
    try {
      // Update local state immediately for UI responsiveness
      setProfileData(prev => ({
        ...prev,
        [field]: value
      }));
      
      // Save to database immediately
      const response = await apiPut('/api/profile', {
        [field]: value
      });
      
      if (!response.ok) {
        let errMsg = 'Failed to save tutorial preference';
        try {
          const errorData = await response.json();
          if (errorData?.error) errMsg = errorData.error;
        } catch (parseError) {
          console.error('Error parsing error response:', parseError);
        }
        throw new Error(errMsg);
      }
      
      // Parse the successful response (optional)
      const result = await response.json();
      console.log('Tutorial preference updated:', result);
      
      // Notify parent component that profile was updated
      if (onProfileUpdate && typeof onProfileUpdate === 'function') {
        onProfileUpdate();
      }
      
    } catch (error) {
      console.error('Error saving tutorial preference:', error);
      setError('Failed to save tutorial preference');
      
      // Revert local state on error
      setProfileData(prev => ({
        ...prev,
        [field]: !value
      }));
    }
  };
  
  const handlePasswordChange = async () => {
    // Clear previous messages
    setPasswordError('');
    setPasswordSuccess('');
    
    // Validate passwords
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }
    
    if (passwordData.newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters long');
      return;
    }
    
    setSaving(true);
    
    try {
      const response = await apiPost('/api/profile/change-password', {
        newPassword: passwordData.newPassword
      });
      
      if (!response.ok) {
        let errMsg = 'Failed to update password';
        try {
          const errorData = await response.json();
          if (errorData?.error) errMsg = errorData.error;
        } catch (parseError) {
          console.error('Error parsing error response:', parseError);
        }
        throw new Error(errMsg);
      }
      
      // Parse the successful response
      const result = await response.json();
      console.log('Password updated successfully:', result);
      
      // Show success message in dialog
      setPasswordSuccess('Password updated successfully. You will be signed out in 3 seconds...');
      
      // Clear password fields
      setPasswordData({
        newPassword: '',
        confirmPassword: ''
      });
      
      // Force logout after a delay to let user see the message
      setTimeout(async () => {
        console.log('Attempting to sign out user after password change...');
        try {
          setPasswordDialog(false); // Close dialog first
          const signOutResult = await signOut();
          
          if (signOutResult?.error) {
            console.error('SignOut returned error:', signOutResult.error);
            throw signOutResult.error;
          }
          
          console.log('SignOut successful, redirecting...');
          // Force redirect to ensure clean state
          window.location.href = '/';
          
        } catch (signOutError) {
          console.error('Error signing out after password change:', signOutError);
          setPasswordDialog(false); // Close dialog even on error
          
          // Force reload as fallback - this will clear all auth state
          console.log('Forcing page reload as fallback...');
          window.location.reload();
        }
      }, 3000);
      
    } catch (error) {
      console.error('Error changing password:', error);
      setPasswordError('Failed to update password. Please try again.');
    } finally {
      setSaving(false);
    }
  };
  
  // Get user display information
  const getUserDisplayName = () => {
    return (profileData.display_name && profileData.display_name.trim()) || 
           user?.email?.split('@')[0] || 
           'User';
  };
  
  const getUserAvatar = () => {
    return user?.user_metadata?.avatar_url || 
           user?.user_metadata?.picture || 
           null;
  };
  
  const getTierDisplayName = (tier) => {
    const tierMap = {
      'beta_tester': 'Beta Tester',
      'early_access': 'Early Access',
      'internal': 'Internal',
      'premium': 'Premium'
    };
    return tierMap[tier] || tier;
  };
  
  const getTierColor = (tier) => {
    const colorMap = {
      'beta_tester': 'primary',
      'early_access': 'secondary',
      'internal': 'warning',
      'premium': 'success'
    };
    return colorMap[tier] || 'default';
  };
  
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Box sx={{ 
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      bgcolor: 'background.default',
      p: 0,
      m: 0
    }}>
      {/* Header */}
      <Box sx={{ 
        p: 3, 
        borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between' 
      }}>
        <Typography variant="h4" sx={{ color: '#0095FF', fontWeight: 600 }}>
          User Profile
        </Typography>
        {onClose && (
          <Button 
            onClick={onClose} 
            variant="outlined"
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              borderColor: 'rgba(255, 255, 255, 0.3)',
              '&:hover': {
                color: '#0095FF',
                borderColor: '#0095FF',
                backgroundColor: 'rgba(0, 149, 255, 0.1)'
              }
            }}
          >
            Close
          </Button>
        )}
      </Box>

      {/* Scrollable Content */}
      <Box sx={{ 
        flex: 1, 
        overflow: 'auto',
        p: 3,
        display: 'flex',
        flexDirection: 'column',
        gap: 3
      }}>
        {/* Error/Success Messages */}
        {error && (
          <Alert 
            severity="error" 
            onClose={() => setError(null)}
            sx={{
              backgroundColor: 'rgba(255, 20, 147, 0.1)',
              border: '1px solid rgba(255, 20, 147, 0.3)',
              color: '#FF1493',
              '& .MuiAlert-icon': { color: '#FF1493' }
            }}
          >
            {error}
          </Alert>
        )}
        {success && (
          <Alert 
            severity="success" 
            onClose={() => setSuccess(null)}
            sx={{
              backgroundColor: 'rgba(0, 149, 255, 0.1)',
              border: '1px solid rgba(0, 149, 255, 0.3)',
              color: '#0095FF',
              '& .MuiAlert-icon': { color: '#0095FF' }
            }}
          >
            {success}
          </Alert>
        )}
        
        {/* User Info Section */}
        <Paper sx={{ 
          backgroundColor: 'rgba(255, 255, 255, 0.02)',
          border: '1px solid rgba(255, 255, 255, 0.05)',
          borderRadius: 3,
          p: 3
        }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Box display="flex" flexDirection="column" alignItems="center">
              <Avatar
                src={getUserAvatar()}
                alt={getUserDisplayName()}
                sx={{ 
                  width: 120, 
                  height: 120, 
                  mb: 2,
                  bgcolor: '#0095FF',
                  fontSize: '2rem',
                  border: '3px solid rgba(0, 149, 255, 0.3)'
                }}
              >
                {!getUserAvatar() && <PersonIcon sx={{ fontSize: '3rem' }} />}
              </Avatar>
              <Typography variant="h6" gutterBottom sx={{ color: 'text.primary', fontWeight: 600 }}>
                {getUserDisplayName()}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {user?.email}
              </Typography>
              {userProfile?.tier && (
                <Chip 
                  label={getTierDisplayName(userProfile.tier)}
                  variant="outlined"
                  sx={{
                    borderColor: '#0095FF',
                    color: '#0095FF',
                    backgroundColor: 'rgba(0, 149, 255, 0.1)'
                  }}
                />
              )}
            </Box>
          </Grid>
          
          <Grid item xs={12} md={8}>
            <Box>
                <Typography variant="h6" sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1, 
                  mb: 3,
                  color: 'text.primary',
                  fontWeight: 600
                }}>
                  <SettingsIcon sx={{ color: '#0095FF' }} /> Profile Information
                </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <TextField
                  label="Display Name"
                  value={profileData.display_name}
                  onChange={(e) => setProfileData({ ...profileData, display_name: e.target.value })}
                  disabled={!isEditing}
                  fullWidth
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'rgba(255, 255, 255, 0.02)',
                      '& fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.1)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.2)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#0095FF',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: 'rgba(255, 255, 255, 0.7)',
                      '&.Mui-focused': {
                        color: '#0095FF',
                      },
                    },
                  }}
                />
                
                <TextField
                  label="Title/Role"
                  value={profileData.title_role}
                  onChange={(e) => setProfileData({ ...profileData, title_role: e.target.value })}
                  disabled={!isEditing}
                  fullWidth
                  variant="outlined"
                  placeholder="e.g., Marketing Manager, Sales Director, CEO..."
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'rgba(255, 255, 255, 0.02)',
                      '& fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.1)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.2)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#0095FF',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: 'rgba(255, 255, 255, 0.7)',
                      '&.Mui-focused': {
                        color: '#0095FF',
                      },
                    },
                  }}
                />

                {/* Action Buttons - Moved below the fields */}
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
                  {!isEditing ? (
                    <Button
                      startIcon={<EditIcon />}
                      onClick={() => setIsEditing(true)}
                      variant="outlined"
                      sx={{
                        color: 'rgba(255, 255, 255, 0.7)',
                        borderColor: 'rgba(255, 255, 255, 0.3)',
                        '&:hover': {
                          color: '#0095FF',
                          borderColor: '#0095FF',
                          backgroundColor: 'rgba(0, 149, 255, 0.1)'
                        }
                      }}
                    >
                      Edit
                    </Button>
                  ) : (
                    <>
                      <Button
                        startIcon={<CancelIcon />}
                        onClick={() => {
                          setIsEditing(false);
                          loadProfileData(); // Reset data
                        }}
                        variant="outlined"
                        sx={{
                          color: 'rgba(255, 255, 255, 0.7)',
                          borderColor: 'rgba(255, 255, 255, 0.3)',
                          '&:hover': {
                            color: '#FF1493',
                            borderColor: '#FF1493',
                            backgroundColor: 'rgba(255, 20, 147, 0.1)'
                          }
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        startIcon={<SaveIcon />}
                        onClick={handleSaveProfile}
                        variant="contained"
                        disabled={saving}
                        sx={{
                          backgroundColor: '#0095FF',
                          color: 'white',
                          '&:hover': {
                            backgroundColor: '#0077CC',
                          },
                          '&:disabled': {
                            backgroundColor: 'rgba(0, 149, 255, 0.3)',
                            color: 'rgba(255, 255, 255, 0.5)'
                          }
                        }}
                      >
                        {saving ? 'Saving...' : 'Save'}
                      </Button>
                    </>
                  )}
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Security Section */}
      <Paper sx={{ 
        backgroundColor: 'rgba(255, 255, 255, 0.02)',
        border: '1px solid rgba(255, 255, 255, 0.05)',
        borderRadius: 3,
        p: 3
      }}>
        <Typography variant="h6" sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1, 
          mb: 3,
          color: 'text.primary',
          fontWeight: 600
        }}>
          <LockIcon sx={{ color: '#0095FF' }} /> Security
        </Typography>
        
        <Button
          variant="outlined"
          onClick={() => {
            setPasswordDialog(true);
            setPasswordError('');
            setPasswordSuccess('');
            setPasswordData({
              newPassword: '',
              confirmPassword: ''
            });
          }}
          startIcon={<LockIcon />}
          sx={{
            color: 'rgba(255, 255, 255, 0.7)',
            borderColor: 'rgba(255, 255, 255, 0.3)',
            '&:hover': {
              color: '#0095FF',
              borderColor: '#0095FF',
              backgroundColor: 'rgba(0, 149, 255, 0.1)'
            }
          }}
        >
          Change Password
        </Button>
      </Paper>
      
      {/* Preferences Section */}
      <Paper sx={{ 
        backgroundColor: 'rgba(255, 255, 255, 0.02)',
        border: '1px solid rgba(255, 255, 255, 0.05)',
        borderRadius: 3,
        p: 3
      }}>
        <Typography variant="h6" gutterBottom sx={{ color: 'text.primary', fontWeight: 600 }}>
          Tutorial Preferences
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Control which tutorials are shown to you
        </Typography>
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={!profileData.hide_list_cleaner_results_tutorial}
                onChange={(e) => handleTutorialToggle('hide_list_cleaner_results_tutorial', !e.target.checked)}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: '#0095FF',
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: '#0095FF',
                  },
                }}
              />
            }
            label="Show List Cleaner Tutorial"
            sx={{ color: 'text.primary' }}
          />
          
          <FormControlLabel
            control={
              <Switch
                checked={!profileData.hide_activity_history_tutorial}
                onChange={(e) => handleTutorialToggle('hide_activity_history_tutorial', !e.target.checked)}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: '#0095FF',
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: '#0095FF',
                  },
                }}
              />
            }
            label="Show Activity History Tutorial"
            sx={{ color: 'text.primary' }}
          />
        </Box>
      </Paper>
      </Box>
      
      {/* Password Change Dialog */}
      <Dialog 
        open={passwordDialog} 
        onClose={() => {
          if (!saving && !passwordSuccess) {
            setPasswordDialog(false);
            setPasswordError('');
            setPasswordSuccess('');
          }
        }} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'background.paper',
            border: '1px solid rgba(255, 255, 255, 0.05)',
            borderRadius: 3
          }
        }}
      >
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {/* Password error message */}
            {passwordError && (
              <Alert 
                severity="error" 
                onClose={() => setPasswordError('')}
                sx={{
                  backgroundColor: 'rgba(255, 20, 147, 0.1)',
                  border: '1px solid rgba(255, 20, 147, 0.3)',
                  color: '#FF1493',
                  '& .MuiAlert-icon': { color: '#FF1493' }
                }}
              >
                {passwordError}
              </Alert>
            )}
            
            {/* Password success message */}
            {passwordSuccess && (
              <Alert 
                severity="success"
                sx={{
                  backgroundColor: 'rgba(0, 149, 255, 0.1)',
                  border: '1px solid rgba(0, 149, 255, 0.3)',
                  color: '#0095FF',
                  '& .MuiAlert-icon': { color: '#0095FF' }
                }}
              >
                {passwordSuccess}
              </Alert>
            )}
            
            {/* Password input fields - hide when success message is shown */}
            {!passwordSuccess && (
              <>
                <TextField
                  label="New Password"
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                  fullWidth
                  variant="outlined"
                  error={!!passwordError && passwordError.includes('at least 6')}
                />
                
                <TextField
                  label="Confirm New Password"
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  fullWidth
                  variant="outlined"
                  error={!!passwordError && passwordError.includes('do not match')}
                />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          {passwordSuccess ? (
            <Button 
              onClick={() => setPasswordDialog(false)} 
              variant="contained"
              sx={{
                backgroundColor: '#0095FF',
                color: 'white',
                '&:hover': {
                  backgroundColor: '#0077CC',
                }
              }}
            >
              Close
            </Button>
          ) : (
            <>
              <Button 
                onClick={() => {
                  setPasswordDialog(false);
                  setPasswordError('');
                  setPasswordSuccess('');
                }}
                sx={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  '&:hover': {
                    color: '#FF1493',
                    backgroundColor: 'rgba(255, 20, 147, 0.1)'
                  }
                }}
              >
                Cancel
              </Button>
              <Button 
                onClick={handlePasswordChange}
                variant="contained"
                disabled={saving || !passwordData.newPassword || !passwordData.confirmPassword}
                sx={{
                  backgroundColor: '#0095FF',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#0077CC',
                  },
                  '&:disabled': {
                    backgroundColor: 'rgba(0, 149, 255, 0.3)',
                    color: 'rgba(255, 255, 255, 0.5)'
                  }
                }}
              >
                {saving ? 'Updating...' : 'Update Password'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserProfile;